import { useState, useMemo } from 'react'
import CloseIcon from '../../assets/icons/close.svg?react';
import SideBoltIcon from '../../assets/icons/side-bolt.svg?react';
import RegionSelector from '../../pages/MapDashboard/components/RegionSelector';
import Accordion from '../Accordion';
import ChargerChart from './ChargerChart';
import { useDRResources } from '../../hooks/queries/drResourceQueries';
import { useChargersByStation } from '../../hooks/queries/chargerQueries';
import { useChargingHistoryTimeSlot } from '../../hooks/queries/chargingHistoryQueries';
import { usePeriodChargerStatTwoWeeks, usePeriodChargerStatOneYear } from '../../hooks/queries/periodChargerStatQueries';
import jejuRegionData from '../../assets/jeju_region_en.json';
import dayjs from 'dayjs';
import './ChargerCompModal.css';

const ChargerCompModal = ({ onClose}) => {
  
  const [selectedRegion, setSelectedRegion] = useState({
    sido: '제주특별자치도',
    sigungu: '',
    eupmyeondong: ''
  });
  
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedDRResource, setSelectedDRResource] = useState(null);
  const [selectedChargerA, setSelectedChargerA] = useState(null);
  const [selectedChargerB, setSelectedChargerB] = useState(null);
  const [activeTab, setActiveTab] = useState('24hours');
  
  // DR 자원 쿼리 파라미터 (RegionSelector 필터링용)
  const drResourceParams = useMemo(() => {
    const params = {};
    if (selectedRegion.sido) params.RegionSido = selectedRegion.sido;
    if (selectedRegion.sigungu) params.RegionSiGunGu = selectedRegion.sigungu;
    if (selectedRegion.eupmyeondong) params.RegionEupMyuonDong = selectedRegion.eupmyeondong;
    return params;
  }, [selectedRegion]);
  
  // DR 자원 데이터 조회 (필터링 적용)
  const { data: drResourcesData, isLoading: drResourcesLoading } = useDRResources(drResourceParams);
  
  // 선택된 충전소의 충전기 데이터 조회
  const { data: chargersData, isLoading: chargersLoading, error: chargersError } = useChargersByStation(selectedStation?.CpoStationId);
  
  // 충전기 A 차트 데이터
  const today = dayjs().format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  
  const { data: chargingHistoryTimeSlotA, isLoading: chargingHistoryLoadingA } = useChargingHistoryTimeSlot(
    selectedChargerA?.ChargerSysId, 
    yesterday
  );
  const { data: periodChargerStatTwoWeeksA, isLoading: twoWeeksLoadingA } = usePeriodChargerStatTwoWeeks(
    selectedChargerA?.ChargerSysId, 
    today
  );
  const { data: periodChargerStatOneYearA, isLoading: oneYearLoadingA } = usePeriodChargerStatOneYear(
    selectedChargerA?.ChargerSysId, 
    today
  );
  
  // 충전기 B 차트 데이터
  const { data: chargingHistoryTimeSlotB, isLoading: chargingHistoryLoadingB } = useChargingHistoryTimeSlot(
    selectedChargerB?.ChargerSysId, 
    yesterday
  );
  const { data: periodChargerStatTwoWeeksB, isLoading: twoWeeksLoadingB } = usePeriodChargerStatTwoWeeks(
    selectedChargerB?.ChargerSysId, 
    today
  );
  const { data: periodChargerStatOneYearB, isLoading: oneYearLoadingB } = usePeriodChargerStatOneYear(
    selectedChargerB?.ChargerSysId, 
    today
  );
  
  
  const sigunguList = useMemo(() => {
    if (!selectedRegion.sido) return [];
    const filtered = jejuRegionData.filter(item => item.RegionSido === selectedRegion.sido);
    const sigunguSet = [...new Set(filtered.map(item => item.RegionSigungu))].sort();
    return sigunguSet;
  }, [selectedRegion.sido]);
  
  // 읍면동 목록 (선택된 시군구에 따라)
  const eupmyeondongList = useMemo(() => {
    if (!selectedRegion.sigungu) return [];
    const filtered = jejuRegionData.filter(item => 
      item.RegionSido === selectedRegion.sido && 
      item.RegionSigungu === selectedRegion.sigungu
    );
    const eupmyeondongSet = [...new Set(filtered.map(item => item.RegionEupMyuonDong))].sort();
    return eupmyeondongSet;
  }, [selectedRegion.sido, selectedRegion.sigungu]);
  
  const handleRegionSelect = (region) => {
    setSelectedRegion(region);
  };
  
  // 아코디언 데이터 구성
  const accordionData = useMemo(() => {
    if (!drResourcesData?.Items) return [];
    
    return drResourcesData.Items.map((drResource) => ({
      id: drResource.DrResourceSysId,
      title: drResource.ResourceName || 'DR자원 이름 없음',
      badge: `${drResource.Stations?.length || 0}개`,
      children: drResource.Stations?.map((station) => ({
        id: station.StationSysId,
        title: station.CpoStationName || '충전소 이름 없음',
        subtitle: station.CpoStationId || '',
        badge: `${station.Chargers?.length || 0}대`,
        data: station // 원본 데이터 보관
      })) || []
    }));
  }, [drResourcesData]);
  
  const handleAccordionItemClick = (item, itemIndex) => {
    setSelectedStation(item.data);
    
    // 해당 충전소가 속한 DR자원 정보 찾기
    const parentDRResource = drResourcesData?.Items?.find(drResource => 
      drResource.Stations?.some(station => station.StationSysId === item.data.StationSysId)
    );
    setSelectedDRResource(parentDRResource);
    
    // 충전기 선택은 유지 (초기화하지 않음)
  };
  
  const handleChargerSelect = (charger) => {
    const isChargerA = selectedChargerA?.ChargerSysId === charger.ChargerSysId;
    const isChargerB = selectedChargerB?.ChargerSysId === charger.ChargerSysId;
    
    // 충전기와 함께 충전소, DR자원 정보도 포함하여 저장
    const chargerWithStation = {
      ...charger,
      stationInfo: selectedStation, // 현재 선택된 충전소 정보 추가
      drResourceInfo: selectedDRResource // 현재 선택된 DR자원 정보 추가
    };
    
    if (isChargerA) {
      setSelectedChargerA(selectedChargerB);
      setSelectedChargerB(null);
    } else if (isChargerB) {
      setSelectedChargerB(null);
    } else {
      if (!selectedChargerA) {
        setSelectedChargerA(chargerWithStation);
      } else if (!selectedChargerB) {
        setSelectedChargerB(chargerWithStation);
      } else {
        setSelectedChargerB(chargerWithStation);
      }
    }
  };
  
  return (
    <div className="charger-comp-modal-background">
      <div className="charger-comp-modal-container">
        {/* HEADER */}
        <div className="charger-comp-modal-header">
          <h3>충전기 간 비교분석</h3>
          <button className='close-btn' onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className='charger-comp-modal-content-divide-container'>
          <div className='charger-comp-modal-content-divide-content left'>
            <RegionSelector
              selectedRegion={selectedRegion}
              onRegionSelect={handleRegionSelect}
              jejuRegionData={jejuRegionData}
              sigunguList={sigunguList}
              eupmyeondongList={eupmyeondongList}
            />

            <div className='charger-comp-modal-category-select-container'>
              <div className='charger-comp-modal-category-select-header p-16 flex flex-col gap-10'>
                <h3 className='text-b1-700 text-base'>DR자원 및 충전소 선택</h3>
                <p className='text-c2-400 text-gray-600'>
                  <b>{drResourcesData?.Items?.length || 0}</b> 개의 데이터가 있습니다.
                </p>
              </div>
              <div className='charger-comp-modal-category-select-list'>
                {drResourcesLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>DR 자원 목록을 불러오는 중...</div>
                ) : (
                  <Accordion
                    items={accordionData}
                    defaultOpenIndex={0}
                    onItemClick={handleAccordionItemClick}
                    selectedItemId={selectedStation?.StationSysId}
                    className="charger-comp-accordion"
                  />
                )}
              </div>
            </div>

            <div className='charger-comp-modal-category-select-container'>
              <div className='charger-comp-modal-category-select-header p-16 flex flex-col gap-10'>
                <h3 className='text-b1-700 text-base'>충전기 선택</h3>
                <p className='text-c2-400 text-gray-600'>
                  <b>{chargersData?.Items?.length || 0}</b> 개의 데이터가 있습니다.
                </p>
              </div>
              <div className='charger-comp-modal-category-select-list'>
                {selectedStation ? (
                  <div className="charger-list">
                    {chargersError ? (
                      <div className="error" style={{ textAlign: 'center', padding: '20px', color: 'var(--error)' }}>충전기 목록을 불러오는데 실패했습니다: {chargersError.message}</div>
                    ) : chargersLoading ? (
                      <div className="loading" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>충전기 목록을 불러오는 중...</div>
                    ) : chargersData?.Items?.length > 0 ? (
                      <>
                        {chargersData.Items.map((charger) => {
                          const isChargerA = selectedChargerA?.ChargerSysId === charger.ChargerSysId;
                          const isChargerB = selectedChargerB?.ChargerSysId === charger.ChargerSysId;
                          const isSelected = isChargerA || isChargerB;

                          return (
                            <div
                              key={charger.ChargerSysId}
                              className={`charger-comp-modal-charger-item ${isSelected ? 'selected' : ''} ${isChargerA ? 'charger-a' : ''} ${isChargerB ? 'charger-b' : ''}`}
                              onClick={() => handleChargerSelect(charger)}
                            >
                              <div className="charger-item-info">
                                <div className={`charger-item-main ${isSelected ? 'selected' : ''}`}>
                                  충전기 {charger.CpoChargerId || 'ID 없음'} / {charger.ChargerCapacity || 0}kW
                                </div>
                              </div>
                              <div className={`selection-indicator ${isChargerA ? 'charger-a' : ''} ${isChargerB ? 'charger-b' : ''}`}>
                                {isChargerA ? 'A' : isChargerB ? 'B' : ''}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div className="no-data" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                        충전기가 없습니다.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-selection" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius)' }}>충전소를 먼저 선택해주세요.</div>
                )}
              </div>
            </div>
          </div>
          <div className='charger-comp-modal-content-divide-content right'>
            <table className="charger-comparison-table">
              <thead>
                <tr>
                  <th width="20%">선택한 충전기</th>
                  <th width="40%">
                    {selectedChargerA ? (
                      <div className="charger-header">
                        <div className="charger-icon charger-a">
                          <div className="charger-icon-container charger-a">
                            <SideBoltIcon />
                          </div>
                        </div>
                        <div className="charger-name charger-a">{selectedChargerA?.stationInfo?.CpoStationId}-{selectedChargerA.CpoChargerId}</div>
                        <button 
                          className="charger-close-btn"
                          onClick={() => setSelectedChargerA(null)}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ) : (
                      <div className="charger-header">
                        <div className="charger-icon">
                          <div className="charger-icon-container">
                            <SideBoltIcon />
                          </div>
                        </div>
                        <div className="charger-name">충전기 A</div>
                      </div>
                    )}
                  </th>
                  <th width="40%">
                    {selectedChargerB ? (
                      <div className="charger-header">
                        <div className="charger-icon charger-b">
                          <div className="charger-icon-container charger-b">
                            <SideBoltIcon />
                          </div>
                        </div>
                        <div className="charger-name charger-b">{selectedChargerB?.stationInfo?.CpoStationId}-{selectedChargerB.CpoChargerId}</div>
                        <button 
                          className="charger-close-btn"
                          onClick={() => setSelectedChargerB(null)}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ) : (
                      <div className="charger-header">
                        <div className="charger-icon">
                          <div className="charger-icon-container">
                            <SideBoltIcon />
                          </div>
                        </div>
                        <div className="charger-name">충전기 B</div>
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>충전기 고유식별자</th>
                  <td>{selectedChargerA?.CpoChargerId || '-'}</td>
                  <td>{selectedChargerB?.CpoChargerId || '-'}</td>
                </tr>
                <tr>
                  <th>충전기 용량</th>
                  <td>{selectedChargerA?.ChargerCapacity ? `${selectedChargerA?.ChargerCapacity}kW` : '-'}</td>
                  <td>{selectedChargerB?.ChargerCapacity ? `${selectedChargerB?.ChargerCapacity}kW` : '-'}</td>
                </tr>
                <tr>
                  <th>충전소명</th>
                  <td>
                    {selectedChargerA?.stationInfo ? 
                      `${selectedChargerA.stationInfo.CpoStationName || '-'}` : 
                      '-'
                    }
                  </td>
                  <td>
                    {selectedChargerB?.stationInfo ? 
                      `${selectedChargerB.stationInfo.CpoStationName || '-'}` : 
                      '-'
                    }
                  </td>
                </tr>
                <tr>
                  <th>충전소 고유식별자</th>
                  <td>{selectedChargerA?.stationInfo?.CpoStationId || '-'}</td>
                  <td>{selectedChargerB?.stationInfo?.CpoStationId || '-'}</td>
                </tr>
                <tr>
                  <th>충전소 주소</th>
                  <td>{selectedChargerA?.stationInfo?.Address || '-'}</td>
                  <td>{selectedChargerB?.stationInfo?.Address || '-'}</td>
                </tr>
                <tr>
                  <th>충전소 계량기 계약번호</th>
                  <td>{selectedChargerA?.stationInfo?.PowerMeterContractNumber || '-'}</td>
                  <td>{selectedChargerB?.stationInfo?.PowerMeterContractNumber || '-'}</td>
                </tr>
                <tr>
                  <th>DR자원이름</th>
                  <td>
                    {selectedChargerA?.drResourceInfo ? 
                      `${selectedChargerA.drResourceInfo.ResourceName || '-'}` : 
                      '-'
                    }
                  </td>
                  <td>
                    {selectedChargerB?.drResourceInfo ? 
                      `${selectedChargerB.drResourceInfo.ResourceName || '-'}` : 
                      '-'
                    }
                  </td>
                </tr>
              </tbody>
            </table>
              {(selectedChargerA || selectedChargerB) && (
                <div className="charger-charts-comparison">
                  <div className="charger-charts-header">
                    <h3>충전기 성능 비교</h3>
                    <p>선택된 충전기들의 과거 데이터를 비교해보세요</p>
                  </div>
                  
                  {/* 탭 네비게이션 */}
                  <div className="tabs charger-comp-modal-tabs">
                    <button 
                      className={`tab ${activeTab === '24hours' ? 'active' : ''}`}
                      onClick={() => setActiveTab('24hours')}
                    >
                      24시간 충전 이력
                    </button>
                    <button 
                      className={`tab ${activeTab === '2weeks' ? 'active' : ''}`}
                      onClick={() => setActiveTab('2weeks')}
                    >
                      2주 통계
                    </button>
                    <button 
                      className={`tab ${activeTab === '1year' ? 'active' : ''}`}
                      onClick={() => setActiveTab('1year')}
                    >
                      1년 통계
                    </button>
                  </div>
                  
                  {/* 탭 컨텐츠 */}
                  <div className="charger-tab-content">
                    <ChargerChart
                      type={activeTab}
                      isLoadingA={chargingHistoryLoadingA || twoWeeksLoadingA || oneYearLoadingA}
                      isLoadingB={chargingHistoryLoadingB || twoWeeksLoadingB || oneYearLoadingB}
                      error={null}
                      chargingHistoryTimeSlotA={chargingHistoryTimeSlotA}
                      chargingHistoryTimeSlotB={chargingHistoryTimeSlotB}
                      periodChargerStatTwoWeeksA={periodChargerStatTwoWeeksA}
                      periodChargerStatTwoWeeksB={periodChargerStatTwoWeeksB}
                      periodChargerStatOneYearA={periodChargerStatOneYearA}
                      periodChargerStatOneYearB={periodChargerStatOneYearB}
                      chargerA={selectedChargerA}
                      chargerB={selectedChargerB}
                    />
                  </div>
                </div>
              )}
          </div>
            
          </div>
        </div>
      </div>
    );
  };

export default ChargerCompModal;
